import type { CommandKind } from '@/core/Command';
import type { CommandHistoryState, HistoryEntry } from '@/core/CommandHistory';
import type { IconName } from './Icons';

/**
 * Turning the command history into something readable.
 *
 * The engine (`@/core/CommandHistory`) knows the session as a list and a cursor. This module is the
 * whole of the translation from that into rows a person reads — glyphs, wording, elapsed time, the
 * synthetic first row — and it is a separate file from the panel for the reason `PlantManifest` is
 * separate from `PlantManifestPanel`: it is the half worth testing, and it has no business touching
 * the DOM to be tested.
 */

/** Where a row sits relative to the cursor. */
export type HistoryRowState =
  /** Applied, and behind the current position. */
  | 'applied'
  /** The state the document is in right now. Exactly one row per list has this. */
  | 'current'
  /** Undone — still reachable, but not part of the document as it stands. */
  | 'undone';

export interface HistoryRow {
  /**
   * What to pass to `CommandHistory.goto` to return to the state *after* this row's operation. The
   * baseline row is 0, the first real operation is 1, and so on — so this is deliberately not an
   * index into {@link HistoryEntry}[].
   */
  readonly index: number;
  /** Stable key for the DOM, so a repaint does not throw away the row under the pointer. */
  readonly key: string;
  readonly label: string;
  readonly kind: CommandKind;
  readonly state: HistoryRowState;
  /** Secondary line: how long ago, and whether the entry stands for more than one step. */
  readonly detail: string;
  /** The baseline row, which stands for the state before anything was done rather than for a command. */
  readonly isBaseline: boolean;
}

/** The glyph per kind of edit. See {@link CommandKind} for why the kinds are coarse. */
const KIND_ICONS: Record<CommandKind, IconName> = {
  plant: 'plant',
  remove: 'delete',
  transform: 'move',
  paint: 'paint',
  sculpt: 'sculpt',
  structure: 'layers',
  fauna: 'fauna',
  tag: 'tag',
  environment: 'sun',
  // The wand's results arrive as composites, and so does anything else assembled from several
  // owners — the four-panel glyph reads as "these, as one thing".
  composite: 'group',
  edit: 'select',
};

/** How each kind is described in a row's tooltip. */
const KIND_LABELS: Record<CommandKind, string> = {
  plant: 'Planting',
  remove: 'Removal',
  transform: 'Move, turn or grow',
  paint: 'Ground paint',
  sculpt: 'Terrain',
  structure: 'Structure',
  fauna: 'Fauna',
  tag: 'Meaning',
  environment: 'Environment',
  composite: 'Several changes as one',
  edit: 'Edit',
};

export function historyIcon(kind: CommandKind): IconName {
  return KIND_ICONS[kind] ?? 'select';
}

export function historyKindLabel(kind: CommandKind): string {
  return KIND_LABELS[kind] ?? 'Edit';
}

/**
 * How long ago something happened, in the coarsest unit that is still true.
 *
 * Relative rather than clock time on purpose: the history is a record of *this sitting*, and "8 min
 * ago" answers the question being asked of it — how far back was that — where "14:32" makes the
 * reader do the subtraction. It is also why there is no date here at all.
 */
export function formatHistoryTime(at: number, now: number): string {
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

/**
 * The first row: the site as this session found it.
 *
 * It exists so that "undo everything" is a place you can click rather than a key you hold down, and
 * because a list whose first row is already an edit gives no name to the state that edit changed.
 *
 * Its wording depends on whether the cap has eaten the beginning. Calling it "Site as opened" once
 * the oldest hundred operations have been dropped would be a lie in the one place a user is relying
 * on the list to be complete, so when the record is partial it says so instead.
 */
function baselineRow(cursor: number, truncated: boolean): HistoryRow {
  return {
    index: 0,
    key: 'baseline',
    label: truncated ? 'Earliest kept step' : 'Site as opened',
    kind: 'edit',
    state: cursor === 0 ? 'current' : 'applied',
    detail: truncated
      ? 'Older operations have been dropped'
      : 'Before the first change of this session',
    isBaseline: true,
  };
}

export interface HistoryRowOptions {
  /** Clock for the elapsed-time line. Defaults to now. */
  now?: number;
  /** Whether the oldest operations have been dropped by the depth cap. */
  truncated?: boolean;
}

/**
 * The session as a list of rows, oldest first — the order Paint.NET, Photoshop and every other
 * history window use, and the order the operations actually happened in.
 *
 * Rows at and after the cursor are `'undone'`: still listed, greyed rather than removed, because
 * they are still reachable and hiding them would make redo look like it came from nowhere.
 */
export function historyRows(
  entries: readonly HistoryEntry[],
  cursor: number,
  options: HistoryRowOptions = {},
): HistoryRow[] {
  const now = options.now ?? Date.now();
  const position = Math.min(Math.max(Math.trunc(cursor), 0), entries.length);
  const rows: HistoryRow[] = [baselineRow(position, options.truncated === true)];

  entries.forEach((entry, i) => {
    const index = i + 1;
    rows.push({
      index,
      key: `e${entry.id}`,
      label: entry.label,
      kind: entry.kind,
      state: index < position ? 'applied' : index === position ? 'current' : 'undone',
      detail:
        entry.steps > 1
          ? `${formatHistoryTime(entry.at, now)}  ·  ${entry.steps} steps merged`
          : formatHistoryTime(entry.at, now),
      isBaseline: false,
    });
  });

  return rows;
}

/** The header line: where in the session the document currently stands. */
export function historySummary(state: CommandHistoryState): string {
  if (state.count === 0) return 'Nothing done yet';
  const undone = state.count - state.cursor;
  const steps = `${state.cursor} of ${state.count} step${state.count === 1 ? '' : 's'}`;
  return undone > 0 ? `${steps}  ·  ${undone} undone` : steps;
}

/** Tooltip for a row, saying both what it is and what clicking it will do. */
export function historyRowTitle(row: HistoryRow): string {
  const what = row.isBaseline ? row.label : `${historyKindLabel(row.kind)} — ${row.label}`;
  if (row.state === 'current') return `${what}  ·  where you are now`;
  return `${what}  ·  click to ${row.state === 'undone' ? 'redo to here' : 'go back to here'}`;
}
